import React, { useState } from 'react';
import './BusinessCard.css';

function BusinessCard({ business, onCall, onViewMap }) {
  const [expandedBusiness, setExpandedBusiness] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showMapModal, setShowMapModal] = useState(false);

  const toggleExpanded = () => {
    setExpandedBusiness(!expandedBusiness);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % business.photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev === 0 ? business.photos.length - 1 : prev - 1
    );
  };

  const getMapsUrl = () => {
    if (business.latitude && business.longitude) {
      return `https://maps.google.com/?q=${business.latitude},${business.longitude}`;
    }
    return `https://maps.google.com/?q=${encodeURIComponent(business.address)}`;
  };

  const getEmbedMapUrl = () => {
    if (business.latitude && business.longitude) {
      return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${business.longitude}!3d${business.latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0:0x0!2z${business.latitude},${business.longitude}!5e0!3m2!1sen!2sin!4v1234567890`;
    }
    return null;
  };

  return (
    <>
      <div className={`business-card ${expandedBusiness ? 'expanded' : ''}`}>
        {/* Photo Gallery */}
        {business.photos && business.photos.length > 0 && (
          <div className="photo-gallery">
            <img 
              src={business.photos[currentPhotoIndex]} 
              alt={business.name}
              className="main-photo"
            />
            {business.photos.length > 1 && (
              <>
                <button 
                  className="photo-nav prev"
                  onClick={prevPhoto}
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <button 
                  className="photo-nav next"
                  onClick={nextPhoto}
                  aria-label="Next photo"
                >
                  ›
                </button>
                <div className="photo-counter">
                  {currentPhotoIndex + 1}/{business.photos.length}
                </div>
              </>
            )}
          </div>
        )}

        {/* Business Header */}
        <div 
          className="business-header"
          onClick={toggleExpanded}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') toggleExpanded();
          }}
          aria-expanded={expandedBusiness}
        >
          <div className="business-info">
            <h3 className="business-name">{business.name}</h3>
            <p className="business-type">{business.type}</p>
            {business.description && (
              <p className="business-description">{business.description}</p>
            )}
          </div>
          <span className="expand-icon">
            {expandedBusiness ? '−' : '+'}
          </span>
        </div>

        {/* Business Details */}
        <div className="business-details">
          {/* Address */}
          <div className="detail-item">
            <span className="detail-icon">📍</span>
            <div>
              <p className="detail-label">Address</p>
              <p className="detail-value">{business.address}</p>
            </div>
          </div>

          {/* Phone */}
          <div className="detail-item">
            <span className="detail-icon">📞</span>
            <div>
              <p className="detail-label">Phone</p>
              <a 
                href={`tel:${business.phone}`} 
                className="detail-value phone-link"
              >
                {business.phone}
              </a>
            </div>
          </div>

          {/* Hours */}
          <div className="detail-item">
            <span className="detail-icon">⏰</span>
            <div>
              <p className="detail-label">Hours</p>
              <p className="detail-value">{business.hours}</p>
            </div>
          </div>

          {/* Email */}
          {business.email && (
            <div className="detail-item">
              <span className="detail-icon">✉️</span>
              <div>
                <p className="detail-label">Email</p>
                <a 
                  href={`mailto:${business.email}`}
                  className="detail-value phone-link"
                >
                  {business.email}
                </a>
              </div>
            </div>
          )}

          {/* Website */}
          {business.website && (
            <div className="detail-item">
              <span className="detail-icon">🌐</span>
              <div>
                <p className="detail-label">Website</p>
                <a 
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-value phone-link"
                >
                  Visit Website
                </a>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <a 
              href={getMapsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="action-button primary"
            >
              📍 View Map
            </a>
            <a 
              href={`tel:${business.phone}`}
              className="action-button secondary"
            >
              📞 Call Now
            </a>
            {getEmbedMapUrl() && (
              <button 
                onClick={() => setShowMapModal(true)}
                className="action-button tertiary"
              >
                🗺️ Embedded Map
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map Modal */}
      {showMapModal && getEmbedMapUrl() && (
        <div className="map-modal-overlay">
          <div className="map-modal">
            <button 
              className="map-close"
              onClick={() => setShowMapModal(false)}
            >
              ✕
            </button>
            <h3>{business.name}</h3>
            <iframe
              title={`${business.name} map`}
              width="100%"
              height="400"
              frameBorder="0"
              src={getEmbedMapUrl()}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      )}
    </>
  );
}

export default BusinessCard;
